<script setup lang="ts">
import { computed, ref } from 'vue';
import { MMText, useMedia } from '@mm-frontend/mm-web-ui-kit';
import {
  InputCounter,
  EButtonSize,
  IconFavorite,
  IconFavoriteFilled,
  IconCart,
  ButtonPrimaryAccented,
  ButtonSecondaryNeutral,
} from '@/features/ui-kit';
import { ERouteName } from '@/app/router/routeName.enum';

import { useCart } from '@/features/store/cart';
import { useProductTracker } from '../../business/trackers';
import { formatDeliveryDateWithFallback } from '@/shared/helpers';

import { useProductPage } from '../../business/use-product-page';

const { isMobileOrTablet } = useMedia();
const { state, likeProduct, setCurrentSkuAmount, skuQntyUiState } = useProductPage();
const { addProductToCartLite } = useCart();
const { trackProductToCartClick, trackProductReduceItemCount } = useProductTracker();

// TODO: Возможно, трекинг избранного (Favorites) должен лежать в скоупе отдельной фичи "Tracking Favorites"
// См. задачу: https://track.magnit.ru/browse/B2CFRONT-6698

// Get data from state
const currentSku = computed(() => state.currentSku);
const product = computed(
  () =>
    state.product || {
      cardNearestDeliveryInfo: { nearestDelivery: null as string | null },
    }
);

const isFavourite = computed(() => state.isFavorite);
const hasAvailableAmount = computed(() => (currentSku.value?.availableAmount || 0) > 0);

const buttonText = computed(() => {
  if (!hasAvailableAmount.value) return 'Нет в наличии';
  if (isMobileOrTablet.value) return 'В корзину';
  return 'Добавить в корзину';
});

const isCounterFocused = ref(false);
const isInitButtonShown = computed(() => !isCounterFocused.value && !skuQntyUiState.value);

const onCountChange = async (amount: number) => {
  if (!state.product || !currentSku.value) return;

  // Track any decrement click (including down to zero)
  if (amount < skuQntyUiState.value) {
    trackProductReduceItemCount(
      state.product.id?.toString() || '',
      currentSku.value.id?.toString() || '',
      getTrackingParams()
    );
  }
  // Note: trackProductToCartClick is now only called from the main "Add to cart" button

  // Update cart amount
  await setCurrentSkuAmount(amount);
};

const isLoading = ref(false);

// Helper function to prepare tracking parameters
const getTrackingParams = () => {
  if (!state.product || !currentSku.value) return {};

  const product = state.product;
  const sku = currentSku.value;

  // Prepare product parameters (selected characteristics)
  const product_parameters =
    sku.characteristics?.reduce(
      (acc, char) => {
        const value =
          (char.value as any)?.title ||
          (char.value as any)?.value ||
          (char.value as any)?.text ||
          '';
        acc[char.title] = value;
        return acc;
      },
      {} as Record<string, string>
    ) || {};

  // Prepare product parameters list (all available values from product.characteristics)
  const product_parameters_list =
    product.characteristics?.reduce(
      (acc, char: any) => {
        acc[char.title] =
          char.values?.map((opt: any) => opt.title || opt.text || opt.value || '') || [];
        return acc;
      },
      {} as Record<string, string[]>
    ) || {};

  // Prepare is_picture_list (which photos exist)
  const is_picture_list = product.photos?.map(() => true) || [];

  // Prepare badges string
  const badges = product.badges?.map((badge) => badge.text).join(';') || '';

  // Get source from history state, then document.referrer, fallback to 'unknown'
  const getSource = () => {
    if (typeof window !== 'undefined') {
      // Try to get from history state (if available)
      if (window.history.state && typeof window.history.state === 'object') {
        const historyState = window.history.state;
        // Check if there's referrer info in history state
        if (historyState.referrer && historyState.referrer.trim() !== '') {
          return historyState.referrer;
        }
        if (historyState.from && historyState.from.trim() !== '') {
          return historyState.from;
        }
        if (historyState.source && historyState.source.trim() !== '') {
          return historyState.source;
        }
      }

      // Try document.referrer as fallback
      if (document.referrer && document.referrer.trim() !== '') {
        return document.referrer;
      }
    }

    return 'unknown';
  };

  return {
    product_parameters,
    product_parameters_list,
    rating_value: product.rating,
    source: getSource(),
    is_picture_list,
    old_price: (sku.fullPrice || 0) / 100, // Convert from kopecks to rubles
    price: (sku.purchasePrice || 0) / 100, // Convert from kopecks to rubles
    rev_num: product.feedbackQuantity || 0,
    wishlist_toggle: state.isFavorite || false,
    badges,
    shop_id: product.seller?.id,
    is_parsing_rating: product.externalRating || false,
    badges_id: product.badges?.map((badge) => badge.id).join(',') || '', // All badge IDs as comma-separated string
    clickable_badges: product.badges?.some((badge) => badge.clickable) || false,
  };
};

async function addToCart() {
  if (!currentSku.value || !state.product || !currentSku.value.id) return;

  isLoading.value = true;
  try {
    const productForCart = {
      ...state.product,
      id: String(state.product.id),
      skuId: String(currentSku.value.id), // Добавляем skuId из currentSku
      badges: state.product.badges as any[],
      characteristics: state.product.characteristics as any[],
      photos: state.product.photos as any[],
      category: {
        ...state.product.category,
        parentList: state.product.category?.parentList as any[],
      },
    };
    await addProductToCartLite(productForCart, 1);

    // Track the add to cart event
    trackProductToCartClick(
      state.product.id?.toString() || '',
      currentSku.value.id.toString(),
      getTrackingParams()
    );

    console.log('Товар добавлен в корзину');
  } catch (error) {
    console.error('Ошибка при добавлении в корзину:', error);
  } finally {
    isLoading.value = false;
  }
}

function goToCart() {
  const router = useRouter();
  router.push({ name: ERouteName.CART });
}

async function toggleFavorite() {
  if (!state.product) return;

  try {
    await likeProduct();
  } catch (error) {
    console.error('Ошибка при изменении избранного:', error);
  }
}
</script>

<template>
  <div class="add-cart">
    <div class="action-wrapper">
      <ButtonPrimaryAccented
        v-if="isInitButtonShown"
        key="add"
        w100
        :size="isMobileOrTablet ? EButtonSize.L48 : EButtonSize.XL56"
        :disabled="(currentSku?.availableAmount || 0) <= 0"
        :loading="isLoading"
        data-test-id="button__add-to-cart"
        @click="addToCart"
      >
        <div class="btns-text">
          {{ buttonText }}
          <MMText
            font="Caption M/Regular"
            color="inverse-primary"
            style="opacity: 0.75"
            v-if="hasAvailableAmount && product.cardNearestDeliveryInfo?.nearestDelivery"
          >
            Доставим
            {{ formatDeliveryDateWithFallback(product.cardNearestDeliveryInfo?.nearestDelivery) }}
          </MMText>
        </div>
      </ButtonPrimaryAccented>

      <div v-else-if="isMobileOrTablet" class="cart-added-row">
        <ButtonPrimaryAccented
          key="cart1"
          class="add-one"
          :size="EButtonSize.S36"
          :disabled="(currentSku?.availableAmount || 0) <= skuQntyUiState"
          :loading="isLoading"
          data-test-id="button__add-one-mobile"
          @click="onCountChange(skuQntyUiState + 1)"
        >
          +1
        </ButtonPrimaryAccented>
        <ButtonSecondaryNeutral
          key="cart2"
          class="to-cart-mobile"
          :disabled="(currentSku?.availableAmount || 0) <= 0"
          :loading="isLoading"
          data-test-id="button__to-cart-mobile"
          @click="goToCart"
          :size="EButtonSize.S36"
        >
          <IconCart :size="20" />
          Перейти
        </ButtonSecondaryNeutral>
      </div>

      <div v-else class="cart-added-row">
        <InputCounter
          :value="skuQntyUiState"
          :max="currentSku.availableAmount"
          size="xl56"
          @change="onCountChange"
          class="cart-added-row__counter"
        />

        <ButtonPrimaryAccented
          key="cart3"
          :size="EButtonSize.XL56"
          :disabled="!currentSku?.availableAmount"
          :loading="isLoading"
          data-test-id="button__to-cart"
          @click="goToCart"
          w100
        >
          <div class="btns-text">
            <span>В корзине</span>
            <div style="opacity: 0.75; font-size: 12px; line-height: 120%">Перейти</div>
          </div>
        </ButtonPrimaryAccented>
      </div>
    </div>

    <ButtonSecondaryNeutral
      v-if="!isMobileOrTablet"
      class="favorite"
      :size="EButtonSize.XL56"
      data-test-id="button__add-to-favorite"
      @click="toggleFavorite"
    >
      <IconFavoriteFilled v-if="isFavourite" color="accented" />
      <IconFavorite v-else />
    </ButtonSecondaryNeutral>
  </div>
</template>

<style lang="stylus" scoped>
.add-cart
  display flex
  column-gap 12px
  .action-wrapper
    width 100%
    display flex
    align-items center
  >>> .ui-button
    &.favorite
      width 64px
  .counter
    width 100%

.favorite
  font-size 28px

.favorite_active
  --color #E53935 !important
  path
    fill #E53935

.cart-added-row
  display flex
  gap 12px
  width 100%
  &__counter
    max-width 140px
  +Media(Mobile, Tablet)
    justify-content flex-end

.to-cart
  display flex
  flex-direction column
  justify-content center
  &__text
    white-space nowrap
  &__subtext
    color: var(--light-text-inverse-secondary, rgba(255, 255, 255, 0.70));

.to-cart-mobile.to-cart-mobile
  background #FFFFFF
  color: var(--red-55, #FF1919);
  text-align: center;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  border: 1px solid var(--red-55, #FF1919);

.btns-text {
  display flex
  flex-direction column
  white-space nowrap
}
</style>
